import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { OneShotSample } from "../types/oneShotSample";
import customFetch from "../utils/customFetch";

// Add a oneshot sample from a node run instance
export const useAddOneShotSampleFromNodeRun = () => {
  return useMutation<OneShotSample, ErrorEvent, { nodeRunInstanceId: string }>({
    mutationFn: ({ nodeRunInstanceId }) => 
      customFetch(`/v1/oneshot-samples/from-node-run/${nodeRunInstanceId}`, { method: "POST" })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to add oneshot sample from node run ${nodeRunInstanceId}`))
        })
        .catch((err) => toast.error(err.message))
  });
};

// Remove a oneshot sample by node run instance ID
export const useRemoveOneShotSampleFromNodeRun = () => {
  return useMutation<OneShotSample, ErrorEvent, { nodeRunInstanceId: string }>({
    mutationFn: ({ nodeRunInstanceId }) => 
      customFetch(`/v1/oneshot-samples/from-node-run/${nodeRunInstanceId}`, {
        method: "DELETE"
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to remove oneshot sample from node run ${nodeRunInstanceId}`))
        })
        .catch((err) => toast.error(err.message))
  });
};

// Get oneshot samples by node ID
export const useGetOneShotSamplesByNodeId = (nodeId: string) => {
  return useQuery<OneShotSample[], ErrorEvent>({
    queryKey: ['oneShotSamples', nodeId],
    queryFn: () => 
      customFetch(`/v1/oneshot-samples/by-node/${nodeId}`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to fetch oneshot samples for node ${nodeId}`))
        })
        .catch((err) => {
          toast.error(err.message);
          return Promise.reject(err);
        }),
    enabled: !!nodeId,
    refetchOnWindowFocus: false,
  });
};

// Check if a oneshot sample exists for a node run instance
export const useCheckOneShotSampleExists = () => {
  return useMutation<{exists: boolean}, ErrorEvent, { nodeRunInstanceId: string }>({
    mutationFn: ({ nodeRunInstanceId }) => 
      customFetch(`/v1/oneshot-samples/from-node-run/${nodeRunInstanceId}`)
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to check if oneshot sample exists for node run ${nodeRunInstanceId}`))
        })
        .catch((err) => {
          toast.error(err.message);
          return Promise.reject(err);
        })
  });
};

// Add a new mutation to create a one-shot sample
export const useCreateOneShotSample = () => {
  return useMutation<OneShotSample, ErrorEvent, OneShotSample>({
    mutationFn: (sample) => 
      customFetch(`/v1/oneshot-samples`, { 
        method: "POST",
        body: JSON.stringify(sample)
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to create oneshot sample`))
        })
        .catch((err) => {
          toast.error(err.message);
          return Promise.reject(err);
        })
  });
};

// Add a new mutation to remove a oneshot sample by ID
export const useRemoveOneShotSampleFromId = () => {
  return useMutation<OneShotSample, ErrorEvent, { id: string }>({
    mutationFn: ({ id }) => 
      customFetch(`/v1/oneshot-samples/${id}`, {
        method: "DELETE"
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error(`Failed to remove oneshot sample with ID ${id}`))
        })
        .catch((err) => toast.error(err.message))
  });
};
